<template>
  <el-form
      @submit.prevent
      @keydown.enter.prevent
      novalidate
      :model="inventoryAdjustment"
      ref="inventoryAdjustmentForm"
      :rules="rules"
      label-position="top"
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-3/12 m-2">
          <button
              type="button"
              class="flex items-center justify-center w-full p-2 rounded-lg button-normal"
              @click="$router.push($Route.INVENTORY_INVENTORY_ADJUSTMENTS_BULK_INVENTORY_ADJUSTMENT)"
          >
            <span class="flex w-10/12 justify-between items-center">
              <span class="flex justify-center items-center w-full">{{ t('loadAllItems') }}</span>
            </span>
          </button>
        </div>

        <div class="md:w-3/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('inventoryAdjustmentForm')"
          />
        </div>

        <div class="md:w-3/12 m-2">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('inventoryAdjustmentForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section class="my-2">
      <h1>{{ t('sections.info') }}</h1>
      <h2>{{ t('userAdjusting') }}: {{ user.fullName }}</h2>

      <el-form-item :label="t('reason')" class="input-label" prop="reason">
        <el-input
            id="reason"
            v-model="inventoryAdjustment.reason"
            :placeholder="t('placeholders.reason')"
            clearable
            required
        >
        </el-input>
      </el-form-item>

      <el-form-item class="input-label" style="margin: 5px auto">
        <el-checkbox
            id="add-accounting-transaction"
            v-model="inventoryAdjustment.addAccountingTransaction"
        >
          {{ t('addAccountingTransaction') }}
        </el-checkbox>
      </el-form-item>
    </input-section>

    <input-section>
      <h1>{{ t('sections.inventory') }}</h1>
      <el-form-item :label="t('itemName')" class="input-label">
        <div class="flex items-center gap-2 w-full">
          <el-switch
              v-model="multiSelect"
              inline-prompt
              active-text="+"
              inactive-text="="
          />
          <template v-if="!multiSelect">
            <el-autocomplete
                id="item"
                v-model="itermSearchTerm"
                :fetch-suggestions="searchItems"
                value-key="name"
                :trigger-on-focus="false"
                class="w-full"
                @select="addItem"
                @keypress.enter.prevent="pressedEnter = true"
                highlight-first-item
            >
            </el-autocomplete>
          </template>
          <template v-else>
            <el-select
                filterable
                clearable
                class="col-span-3"
                value-key="id"
                placeholder=""
                multiple
                v-model="selectedItems"
                remote
                :remote-method="searchItemsMultiSelect"
            >
              <el-option
                  v-for="option in items"
                  :key="option.id"
                  :label="option.name"
                  :value="option"
              ></el-option>
            </el-select>
            <el-button type="primary" @click="addMultiItems">{{t('add')}}</el-button>
          </template>
        </div>
      </el-form-item>
    </input-section>

    <div class="w-10/12 mt-8" v-if="!inventoryAdjustment.empty()">
      <el-table :data="inventoryAdjustment.inventoryAdjustmentItems"
                style="width: 100%" stripe>
        <el-table-column prop="item.name"
                         width="200"
                         :label="t('itemName')"></el-table-column>

        <el-table-column prop="oldQuantity" :label="t('oldQuantity')"
                         width="100" align="center">
          <template #default="props">
            <div class="el-input el-input--small">
              {{ computedQuantity(props.row) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="newQuantity" :label="t('newQuantity')"
                         width="200" align="center">
          <template #default="props">
            <el-input-number
                v-model="props.row.newQuantity"
                :step="1"
                size="small"
                controls-position="right"
            ></el-input-number>
          </template>
        </el-table-column>
        <el-table-column prop="item.barcode"
                         width="200"
                         :label="t('barcode')"></el-table-column>
        <el-table-column prop="extraProperties.batchNumber" :label="t('batchNumber')"
                         width="200" align="center">
          <template #default="props">
            <el-select v-if="props.row.item.isExpirable" v-model="props.row.extraProperties.batchNumber" :placeholder="t('batchNumber')">
              <template v-for="stock in props.row.item.itemStocks" :key="stock.extraProperties?.batchNumber">
                <el-option v-if="stock.extraProperties?.batchNumber" :label="stock.extraProperties?.batchNumber" :value="stock.extraProperties?.batchNumber" />
              </template>
            </el-select>
          </template>
        </el-table-column>


        <el-table-column
            prop="inventoryAdjustmentItemSerials"
            :label="t('serialNumbers')"
            width="300"
        >
          <template #default="props">
            <template v-if="props.row.item.isSerialized">
              <!-- add new serials -->
              <before-create
                  v-if="props.row.newQuantity > computedQuantity(props.row)"
                  :do="() => props.row.resetInventoryAdjustmentItemSerials()"
              >
                <tag-input
                    :title="
                    t('insertSerials', {
                      num: props.row.newQuantity - computedQuantity(props.row)
                    })
                  "
                    v-model="props.row.inventoryAdjustmentItemSerials"
                    :insertFunction="(val: any) => props.row.insertSerialNumber(val)"
                    :removeFunction="(index: any) => props.row.removeSerialNumber(index)"
                    :valueFormatter="(serialObject: any) => serialObject.itemSerial.number"
                    :validate="() => props.row.validateSerials()"
                />
              </before-create>

              <!-- remove old serials -->
              <div class="input-label"
                   v-else-if="props.row.newQuantity < computedQuantity(props.row)">
                {{
                  t('removeSerials', {
                    num: computedQuantity(props.row) - props.row.newQuantity
                  })
                }}
                <el-select
                    filterable
                    clearable
                    multiple
                    class="w-full"
                    v-model="props.row.toRemoveSerialIDs"
                    :multiple-limit="computedQuantity(props.row) - props.row.newQuantity"
                >
                  <el-option
                      v-for="(item, i) in props.row.item.itemStocks.find((stock: any) => stock.extraProperties?.batchNumber === props.row.extraProperties.batchNumber)?.itemSerials"
                      :key="i"
                      :label="item.number"
                      :value="item.id"
                  >
                    <span :class="{ 'float-right': $ctx.getDir() == 'rtl' }">
                      {{ item.number }}
                    </span>
                  </el-option>
                </el-select>
              </div>
            </template>
          </template>
        </el-table-column>

        <el-table-column width="50" align="center">
          <template #default="scope">
            <span class="text-red-600 cursor-pointer"
                  @click="removeItem(scope.$index)">
              <font-awesome-icon icon="trash"/>
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Cashier PIN Dialog -->
    <el-dialog v-model="cashierPinDialogOpen" width="30%" :title="t('enterCashierPin')">
      <div>
        <!-- Enter the pin -->
        <div class="my-2">
          <p>{{ t('enterCashierPin') }}</p>
        </div>

        <!-- password input up to 4 chars -->
        <div class="my-2">
          <el-input
            id="password"
            v-model="cashierPin"
            type="password"
            required
            show-password
            autofocus
            dir="ltr"
            :maxlength="4"
            :minlength="4"
            @keyup.enter="submitWithPin"
            ref="cashierPinInput"
          />
        </div>

        <!-- submit button -->
        <div class="flex items-center justify-between mb-2">
          <el-button
            type="primary"
            @click="submitWithPin"
            class="w-full"
          >
            {{ t('submitAdjustment') }}
          </el-button>
        </div>
      </div>
    </el-dialog>
  </el-form>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import InventoryAdjustment, {
  inventoryAdjustmentI18nMessages
} from '@/models/inventory/InventoryAdjustment'
import {ElForm} from 'element-plus'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import TagInput from '@/components/form/TagInput.vue'
import Item from '@/models/inventory/Item'
import {useI18n} from 'vue-i18n'
import { useSettingsStore } from '@/stores/settingsStore'

export default defineComponent({
  name: 'new-inventory-receiving',

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          ...inventoryAdjustmentI18nMessages.en,
          itemName: 'Item Name',

          sections: {
            info: 'Meta data',
            inventory: 'Inventory Info'
          },

          placeholders: {
            reason: 'Example: missing a couple of items',
            itemName: 'Enter barcode, sku, or item name'
          },

          userAdjusting: 'Employee responsible for this inventory adjustment',
          insertSerials: 'You must insert {num} new serial numbers',
          removeSerials: 'You must remove {num} old serial numbers',

          successMsg: 'Inventory was adjusted successfully',
          add: 'Add',
          batchNumber: 'Batch Number',
          enterCashierPin: 'Enter Cashier PIN',
          errCashierPinRequired: 'Cashier PIN is required',
          submitAdjustment: 'Submit Adjustment',
          loadAllItems: 'Load All Items',
          barcode: 'Barcode'
        },
        ar: {
          ...inventoryAdjustmentI18nMessages.ar,
          itemName: 'اسم المادة',

          sections: {
            info: 'البيانات الوصفية',
            inventory: 'البضاعة المعدلة'
          },

          placeholders: {
            reason: 'مثلا: بعض البضاعة مفقودة',
            itemName: 'ادخل اسم المنتج او الباركود او SKU'
          },

          userAdjusting: 'الموظف المسؤول عن تعديل البضاعة',
          insertSerials: 'قم بادخال {num} serial numbers',
          removeSerials: 'قم بمسح {num} serial numbers',

          successMsg: 'تم تعديل البضاعة بنجاح',
          add: 'اضافة',
          batchNumber: 'رقم الوجبة',
          enterCashierPin: 'أدخل رقم الموظف',
          errCashierPinRequired: 'رقم الموظف مطلوب',
          submitAdjustment: 'إرسال التعديل',
          loadAllItems: 'تحميل جميع المواد',
          barcode: 'Barcode'
        }
      }
    })

    return {t}
  },

  components: {
    AlertBadge,
    HorizontalButton,
    TagInput,
    InputSection
  },
  data() {
    return {
      inventoryAdjustment: new InventoryAdjustment(),
      itermSearchTerm: '',
      error: {title: '', body: ''},
      serials: [],
      pressedEnter: false,
      user: this.$ctx.currentUser,
      rules: {
        reason: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ]
      },
      multiSelect: false,
      items: [] as Array<Item>,
      selectedItems: [] as Array<Item>,
      cashierPinDialogOpen: false,
      cashierPin: '',
    }
  },

  methods: {
    computedQuantity(row: any) {
      // For expirable items, require batch number selection first
      if (row.item.isExpirable) {
        if (row.extraProperties.batchNumber) {
          return row.item.itemStocks.find((stock: any) => stock.extraProperties?.batchNumber === row.extraProperties.batchNumber)?.quantity || 0
        }
        return 0 // Don't show quantity until batch is selected
      }
      return row.oldQuantity
    },
    addMultiItems() {
      for (const item of this.selectedItems) {
        this.addItem(item);
      }
      this.selectedItems = [];
    },
    async searchItems(query: string, cb: Function) {
      // if an item already exists in the table, don't do remote search
      const cachedItem =
          this.inventoryAdjustment.findByBarcode(query) || this.inventoryAdjustment.findBySKU(query)
      if (cachedItem) {
        this.addItem(cachedItem)
        cb([])
        return
      }

      try {
        const url = `${Item.ENDPOINT}?type=checkout&q=${query}`
        const items = await this.$http.get<Item[]>(url)

        // Filter out unlimited stock items
        const filteredItems = items.filter(item => !item.unlimitedStock)

        // when entering a barcode, the scanner hits enter by default
        // so if we get back one result, we wanna append that right away
        // it makes the process much faster than make the user select by themselves
        if (this.pressedEnter && filteredItems.length === 1) {
          this.addItem(filteredItems[0])
          cb([])
        } else {
          // show them all options
          cb(filteredItems)
        }
      } catch (error: any) {
        this.$alertModal.showDanger({title: error.title})
        cb([])
      }

      this.pressedEnter = false
    },

    async searchItemsMultiSelect(query: string) {
      if (!query) {
        query = ''
      }
      try {
        const url = `${Item.ENDPOINT}?q=${query}`
        const itemsRes = await this.$http.get<Item[]>(url)
        // Filter out unlimited stock items
        this.items = itemsRes
          .filter(item => !item.unlimitedStock)
          .map((item) => Item.from(item))
      } catch (error) {
        this.items = []
      }
    },

    addItem(selected: Item) {
      this.inventoryAdjustment.addItem(selected)
      this.itermSearchTerm = ''
    },

    removeItem(index: number) {
      this.inventoryAdjustment.removeItem(index)
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
      this.inventoryAdjustment = new InventoryAdjustment()
    },

    async submit(ir: InventoryAdjustment, pin: string = '') {
      this.error.title = ''
      this.error.body = ''

      try {
        const headers = pin ? { pin: pin } : {}
        await this.$http.post(InventoryAdjustment.ENDPOINT, ir, { headers })
        await this.$router.push(this.$Route.INVENTORY_INVENTORY_ADJUSTMENTS)
      } catch (error: any) {
        return error
      }
    },

    checkForm() {
      const errors = []
      for (const iai of this.inventoryAdjustment.inventoryAdjustmentItems) {
        errors.push(iai.validateSerials())
        // errors.push(iai.validateQuantity())
      }

      this.error.body = errors.filter((err) => err).join('\n')

      if (this.error.body) {
        this.error.title = this.$t('validation.inputErrors')
        document.getElementById('main')?.scrollTo({top: 0})
        return false
      }

      return true
    },

    async validateSubmit(formName: string) {
      this.inventoryAdjustment.preparePayloadData()
      const form = this.$refs[formName] as typeof ElForm

      form?.validate(async (valid: boolean) => {
        if (!valid || !this.checkForm()) {
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        }

        // Check if PIN is required
        if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
          this.cashierPinDialogOpen = true
          this.$nextTick(() => {
            setTimeout(() => {
              this.getCashierInputRef().focus()
            }, 0)
          })
          return
        }

        // Submit without PIN
        const resp = await this.submit(this.inventoryAdjustment as InventoryAdjustment)

        if (resp) {
          this.error.title = (resp as any)?.title
          this.error.body = (resp as any)?.body
          document.getElementById('main')?.scrollTo({top: 0})
          return false
        } else {
          this.clearForm(formName)
          this.$alertModal.showSuccess({title: this.t('successMsg')})
        }
        return true
      })
    },

    async submitWithPin() {
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (this.cashierPin === '') {
          this.$alertModal.showDanger({
            title: this.t('errCashierPinRequired')
          })
          return
        }
      }

      this.cashierPinDialogOpen = false
      const resp = await this.submit(this.inventoryAdjustment as InventoryAdjustment, this.cashierPin)

      if (resp) {
        this.error.title = (resp as any)?.title || 'Error'
        this.error.body = (resp as any)?.body || 'An error occurred'
        document.getElementById('main')?.scrollTo({ top: 0 })
      } else {
        this.clearForm('inventoryAdjustmentForm')
        this.$alertModal.showSuccess({ title: this.t('successMsg') })
      }

      // Clear PIN for security
      this.cashierPin = ''
    },

    getCashierInputRef() {
      return (this.$refs['cashierPinInput'] as any)
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply p-2 text-lg py-3 font-bold;
}

form > h2 {
  @apply p-2 py-3 font-bold;
}

form > h3 {
  @apply p-2 py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
